import { StyledSelect } from "../primitives/styled-select";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Loader2,
  ShieldAlert,
  Users,
  X,
} from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { asStringArray } from "../lib/valueCoercion";
import { Checkbox } from "../primitives/checkbox";
import { DateTimePicker } from "../primitives/date-time-picker";
import { RecipientsInput } from "./RecipientsInput";
import { type DiffChange, InlineDiffPreview } from "./actions/InlineDiffPreview";
import { type SlideObj, SlideOutlineEditor } from "./actions/SlideOutlineEditor";
import { RichTextEditorLazy } from "./composer-parts/RichTextEditorLazy";
import {
  type AttachableDocumentOption,
  type AttachmentChipValue,
  EmailAttachmentField,
  type SearchAttachableDocuments,
  type UploadAttachment,
  isAttachmentArray,
} from "./email/EmailAttachmentField";

// Attachment types + the staged-upload UI live in ``./email/EmailAttachmentField``
// (single source of truth shared with the inbox reply composer). Re-exported
// here so the action pipeline (``ScalarActionProposalMount`` /
// ``FocusedActionView``) and the ``@ando/ui`` barrel keep their import paths.
export type {
  AttachmentChipValue,
  AttachableDocumentOption,
  SearchAttachableDocuments,
  UploadAttachment,
} from "./email/EmailAttachmentField";

/**
 * ActionFieldEditor — schema-driven editable field renderer for the Action
 * Proposal Drawer AND the manual "Nuova azione" composer (A.7 + June 2026
 * manual-create work).
 *
 * Inspired by v1's `ActionFieldEditor.tsx` (827 LOC) but generalised: instead
 * of a CRM-specific field-spec contract, this component takes a small
 * declarative type schema with optional UI hints (citation marker, format,
 * placeholder, enum options, section grouping, action-time flag) so callers
 * can re-use it for ANY action type (email, calendar, fic invoice, salesforce
 * lead, sap PO, slack message, db write, notion page, …) without forking a
 * vendor-specific editor.
 *
 * Renders one input per field; controlled value lives in the parent (the
 * ActionDrawer reducer holds proposed_fields + edited_fields). The editor
 * never owns persistent state — it is a pure rendering layer.
 *
 * Layout (June 2026): fields are grouped into three render tiers so large
 * professional forms (an Italian invoice, a Salesforce Lead) stay scannable:
 *   1. ACTION-TIME params (``isActionTime``) — rendered in a prominent
 *      amber-accented block at the top with a "Verifica questi campi" heading.
 *      These are the highest-risk free-form inputs (e.g. FiC ``recipient_email``
 *      per `feedback_fic_schedule_email_recipient_visibility.md` — the PDF-leak
 *      guard). They MUST never hide in a flat list.
 *   2. NAMED sections (``section``) — each gets a subtle uppercase heading.
 *   3. UNGROUPED fields — the default flat list (backwards-compatible: when no
 *      field carries ``section``/``isActionTime`` the editor renders exactly
 *      the legacy flat list, so existing previews + tests are unaffected).
 *
 * Per `feedback_hitl_card_editable_fields.md`: every AI-proposed field MUST
 * be editable. Read-only is opt-in via `field.readonly`.
 */

// ============================================================================
// Types
// ============================================================================

export type ActionFieldType =
  | "string"
  | "text" // multi-line plain text (textarea)
  | "richtext" // multi-line formatted text (Tiptap)
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "select"
  | "multiselect" // multiple values — chips (free tags) or option toggles
  | "currency" // EUR
  | "email"
  | "phone"
  | "url"
  | "iban"
  | "vat_id" // Italian Partita IVA
  | "password"
  | "reference" // foreign-key lookup (typeahead)
  | "recipients" // multi-value autocomplete via Google People / MS Graph (string[] of emails)
  | "json" // raw JSON object/array (monospace + parse check)
  | "slide_list" // presentation outline — SlideObj[] (mirror BE FieldType.SLIDE_LIST)
  | "file"; // file attachments — AttachmentChipValue[] (mirror BE FieldType.FILE)

export interface ActionFieldOption {
  readonly metadata?: import("./actions/emailSenderPolicy").EmailSenderMetadata;
  value: string;
  label: string;
}

export interface ActionFieldSpec {
  /** Unique key matching `proposed_fields[key]`. */
  name: string;
  /** Human label shown above the input. */
  label: string;
  type: ActionFieldType;
  /**
   * BE-resolved default value (``UnifiedFieldSpec.value``) — e.g. the
   * auto-selected account when a single connection exists, resolved at
   * schema-build time (AFTER the AI proposal was flattened into
   * ``proposed_fields``). Editors seed from this as the base layer so an
   * auto-selected required field isn't reported empty. ``proposed_fields`` and
   * the user's edits override it.
   */
  value?: ActionFieldValue;
  /**
   * The record's CURRENT value (the "before") for an UPDATE_RECORD proposal.
   * When present AND different from the live edited value, the field renders a
   * compact before→after diff (``current → proposed``) so the operator sees
   * exactly what will change on the CRM / accounting record before confirming.
   * ``undefined`` for every other action type / when the BE couldn't read the
   * live record → no diff, field renders as today. Mirrors BE
   * ``UnifiedFieldSpec.current_value``.
   */
  currentValue?: ActionFieldValue;
  required?: boolean;
  readonly?: boolean;
  placeholder?: string;
  helperText?: string;
  /** Citation marker (e.g. `[1]`) when Ando extracted from a source. */
  citation?: string;
  /**
   * Extraction confidence for an AI-populated field, surfaced as a small dot
   * next to the label so the operator knows which values to double-check:
   * ``"high"`` = calm/ok token, ``"low"`` = warning token. Purely a display
   * hint — fields without it render exactly as before.
   */
  confidence?: "high" | "low";
  /** For `select` / `multiselect`: the available options. */
  options?: ActionFieldOption[];
  /** Min/max for numbers/currency. */
  min?: number;
  max?: number;
  /**
   * Section grouping key (e.g. "Cliente", "Righe", "Pagamento"). Fields that
   * share a ``section`` render under one heading, in first-appearance order.
   * Omit for ungrouped fields.
   */
  section?: string;
  /**
   * Action-time parameter (not a sobject column) — rendered in the prominent
   * top block. The highest-risk free-form inputs. Mirrors BE
   * ``UnifiedFieldSpec.is_action_time``.
   */
  isActionTime?: boolean;
  /** For `reference`: the human name of the target object (e.g. "Account"). */
  referenceTo?: string;
  /**
   * For `recipients`: the connection (= account) whose People API is queried
   * for the autocomplete. ``ActionFieldEditor`` forwards this to the
   * ``loadPeopleOptions`` callback so the parent can pick the right
   * Microsoft Graph / Google People scope. When omitted the renderer falls
   * back to a free-text chip input (still editable, never a crash).
   *
   * By convention this is the ``name`` of the per-capability account picker
   * field elsewhere in the schema (e.g. ``calendar_connection_id``,
   * ``from_account``, ``slack_connection_id``) — its value at render time
   * supplies the connection id to the people search.
   */
  peopleSearchConnectionFieldName?: string;
}

/**
 * A field value. Scalars cover the vast majority of fields; ``string[]`` backs
 * ``multiselect``; ``slide_list`` / structured editors cross the boundary via a
 * local, documented cast (they are type-erased to ``ActionFieldValue`` by the
 * generic contract and re-narrowed at the renderer).
 */
export type ActionFieldValue = string | number | boolean | string[] | null;

export interface ActionFieldError {
  message: string;
}

/**
 * Async typeahead transport for ``reference`` fields. ``@ando/ui`` does not
 * depend on ``@ando/core``, so the host (apps/client) supplies the fetch.
 * Returns ranked options for the partial query. When omitted, ``reference``
 * fields fall back to a free-text input (with a datalist when ``options`` are
 * present) — still editable, never a crash.
 */
export type LoadReferenceOptions = (
  field: ActionFieldSpec,
  query: string,
) => Promise<readonly ActionFieldOption[]>;

/**
 * One people-search hit — the LLM-safe shape returned by the
 * ``GET /people/search`` BE endpoint. ``email`` is the canonical key used
 * for ``recipients`` field values; ``display_name`` drives the chip label;
 * ``provider`` lets the chip render a tiny Google/Microsoft glyph so
 * multi-account users see at a glance whose directory was matched.
 */
export interface PeopleSearchHit {
  readonly email: string;
  readonly display_name: string;
  readonly provider: "google" | "microsoft" | "other";
  readonly job_title?: string;
}

/**
 * Async typeahead transport for ``recipients`` fields. Mirrors the
 * ``loadReferenceOptions`` contract: the host (apps/client) supplies it so
 * ``@ando/ui`` stays free of API client deps. ``connectionId`` is taken from
 * the per-capability account picker field whose name is recorded on the
 * spec as ``peopleSearchConnectionFieldName``. When ``connectionId`` is
 * ``null`` (user hasn't chosen the sender yet) the host should resolve
 * with ``[]`` — the renderer then falls back to free-text chip mode.
 *
 * ``signal`` is an ``AbortSignal`` the renderer passes when a fresh
 * keystroke supersedes the in-flight query. Honour it by forwarding to
 * the underlying fetch / ``apiClient`` call so the stale response is
 * cancelled before it can overwrite the latest hits (race condition
 * flagged in the June 9, 2026 audit).
 */
export type LoadPeopleOptions = (args: {
  readonly query: string;
  readonly connectionId: string | null;
  readonly field: ActionFieldSpec;
  readonly signal?: AbortSignal;
}) => Promise<readonly PeopleSearchHit[]>;

export interface ActionFieldEditorProps {
  /** Schema describing the fields to render. */
  schema: readonly ActionFieldSpec[];
  /** Current value map (controlled). */
  values: Readonly<Record<string, ActionFieldValue>>;
  /** Per-field validation errors (parent supplies after zod parse). */
  errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  /** Emits the full updated value map (parent diffs vs proposed). */
  onChange: (next: Record<string, ActionFieldValue>) => void;
  /** Disable all inputs (e.g. during `executing` state). */
  disabled?: boolean;
  /** Async typeahead resolver for `reference` fields (optional). */
  loadReferenceOptions?: LoadReferenceOptions;
  /**
   * Async people-search resolver for ``recipients`` fields (Gmail / Outlook
   * autocomplete via the BE ``GET /people/search`` endpoint). Wired by the
   * host so ``@ando/ui`` stays free of API-client deps. When omitted, the
   * ``recipients`` renderer degrades to free-text chips — same UX contract
   * as ``multiselect`` tag mode.
   */
  loadPeopleOptions?: LoadPeopleOptions;
  /**
   * Upload transport for ``file`` fields. When omitted the file
   * picker chips render in a read-only state (existing AI-proposed
   * attachments stay visible, but the user can't add new files). Pass a
   * wrapper around ``buildEmailAttachment`` from ``@ando/core`` for the
   * default behaviour.
   */
  uploadAttachment?: UploadAttachment;
  /**
   * Search transport for the "Scegli dai documenti Ando" picker on ``file``
   * fields. When omitted, only local upload is offered. Wired by the host to
   * ``searchAttachableFiles`` (``@ando/core``) so ``@ando/ui`` stays API-free.
   */
  searchAttachableDocuments?: SearchAttachableDocuments;
  className?: string;
  // ── Collaboration (banco field-locks) — all OPTIONAL, default off. ─────────
  /** Fields locked by a PEER (host filters out self), keyed by ``spec.name``.
   * A present entry disables the field and shows a "in modifica da …" badge. */
  lockedFields?: Readonly<Record<string, FieldLockInfo>>;
  /** Focus entered a field's subtree — host acquires the field-lock. */
  onFieldFocus?: (name: string) => void;
  /** Focus left a field's subtree — host releases the field-lock. */
  onFieldBlur?: (name: string) => void;
}

/** Who currently holds a field (display only). */
export interface FieldLockInfo {
  readonly name: string;
  readonly color: string;
  readonly initials?: string;
}

// ============================================================================
// Validation helpers
// ============================================================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/;
const VAT_ID_IT_RE = /^(IT)?\d{11}$/i;
const PHONE_RE = /^[+]?[\d\s().-]{6,20}$/;

function isEmptyValue(value: ActionFieldValue): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Built-in syntactic validation. Caller can override / extend by passing
 * `errors`; this returns `undefined` when the value is acceptable so the
 * parent can still merge zod errors on top.
 */
export function validateField(
  spec: ActionFieldSpec,
  value: ActionFieldValue,
  i18n?: I18n,
): ActionFieldError | undefined {
  // Resolve through Lingui when the caller provides its i18n instance;
  // the bare-descriptor fallback (Italian msgid) keeps the legacy 2-arg
  // signature working for tests and non-React callers.
  const t = (d: MessageDescriptor): string => (i18n ? i18n._(d) : String(d.id));
  if (spec.required && isEmptyValue(value)) {
    return { message: t(msg`Campo obbligatorio`) };
  }
  if (isEmptyValue(value)) return undefined;

  switch (spec.type) {
    case "select":
      if (spec.name === "from_account" && spec.options?.length && !spec.options.some((option) => option.value === value)) {
        return { message: t(msg`Mittente non disponibile. Seleziona un account.`) };
      }
      break;
    case "email":
      if (typeof value !== "string" || !EMAIL_RE.test(value)) {
        return { message: t(msg`Email non valida`) };
      }
      break;
    case "phone":
      if (typeof value !== "string" || !PHONE_RE.test(value.trim())) {
        return { message: t(msg`Numero di telefono non valido`) };
      }
      break;
    case "url":
      if (typeof value !== "string") return { message: t(msg`URL non valido`) };
      try {
        // eslint-disable-line no-new — constructor throws on malformed URLs
        new URL(value);
      } catch {
        return { message: t(msg`URL non valido (includi https://)`) };
      }
      break;
    case "iban":
      if (typeof value !== "string" || !IBAN_RE.test(value.replace(/\s/g, "").toUpperCase())) {
        return { message: t(msg`IBAN non valido`) };
      }
      break;
    case "vat_id":
      if (typeof value !== "string" || !VAT_ID_IT_RE.test(value.replace(/\s/g, ""))) {
        return { message: t(msg`P.IVA non valida (11 cifre)`) };
      }
      break;
    case "json":
      if (typeof value === "string") {
        try {
          JSON.parse(value);
        } catch {
          return { message: t(msg`JSON non valido`) };
        }
      }
      break;
    case "number":
    case "currency": {
      const n = typeof value === "number" ? value : Number(value);
      if (Number.isNaN(n)) return { message: t(msg`Numero non valido`) };
      if (spec.min !== undefined && n < spec.min) return { message: t(msg`Min ${spec.min}`) };
      if (spec.max !== undefined && n > spec.max) return { message: t(msg`Max ${spec.max}`) };
      break;
    }
    default:
      break;
  }
  return undefined;
}

// ============================================================================
// Field renderers (one per type, split for clarity)
// ============================================================================

const inputCls =
  "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-sm text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

interface RendererProps {
  spec: ActionFieldSpec;
  value: ActionFieldValue;
  disabled?: boolean;
  onCommit: (next: ActionFieldValue) => void;
  invalid: boolean;
  fieldId: string;
  loadReferenceOptions?: LoadReferenceOptions;
  loadPeopleOptions?: LoadPeopleOptions;
  uploadAttachment?: UploadAttachment;
  searchAttachableDocuments?: SearchAttachableDocuments;
  /**
   * Resolved value of the per-capability account-picker field (e.g.
   * ``from_account``, ``calendar_connection_id``) that supplies the
   * connection id for ``recipients`` field people-search. ``null`` when
   * the user hasn't picked the sender yet — the renderer then falls back
   * to free-text chips.
   */
  peopleSearchConnectionId?: string | null;
}

const StringRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <input
    id={fieldId}
    type={spec.type === "email" ? "email" : "text"}
    inputMode={spec.type === "email" ? "email" : undefined}
    autoComplete={spec.type === "email" ? "email" : "off"}
    className={cn(inputCls, invalid && "border-destructive focus:ring-destructive")}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  />
);

const PhoneRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <input
    id={fieldId}
    type="tel"
    inputMode="tel"
    autoComplete="tel"
    className={cn(inputCls, invalid && "border-destructive focus:ring-destructive")}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder ?? "+39 02 1234567"}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  />
);

const UrlRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <input
    id={fieldId}
    type="url"
    inputMode="url"
    autoComplete="url"
    className={cn(inputCls, invalid && "border-destructive focus:ring-destructive")}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder ?? "https://"}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  />
);

const PasswordRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => {
  const { i18n } = useLingui();
  const [reveal, setReveal] = React.useState(false);
  return (
    <div className="relative">
      <input
        id={fieldId}
        type={reveal ? "text" : "password"}
        autoComplete="off"
        className={cn(inputCls, "pr-9", invalid && "border-destructive focus:ring-destructive")}
        value={(value as string | null) ?? ""}
        placeholder={spec.placeholder}
        readOnly={spec.readonly}
        disabled={disabled}
        onChange={(e) => onCommit(e.target.value)}
        aria-invalid={invalid || undefined}
      />
      <button
        type="button"
        onClick={() => setReveal((r) => !r)}
        disabled={disabled}
        aria-label={reveal ? i18n._(msg`Nascondi`) : i18n._(msg`Mostra`)}
        className="-translate-y-1/2 absolute top-1/2 right-2 text-ink-3 hover:text-ink-1 disabled:opacity-50"
      >
        {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const TextRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <textarea
    id={fieldId}
    rows={4}
    className={cn(inputCls, "resize-y", invalid && "border-destructive focus:ring-destructive")}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  />
);

const RichTextRenderer: React.FC<RendererProps> = ({ spec, value, disabled, onCommit }) => {
  // Read-only / disabled: render as static text so we don't pull Tiptap into a
  // non-editable view (and to keep the disabled affordance unambiguous).
  if (spec.readonly || disabled) {
    return (
      <div className={cn(inputCls, "min-h-[92px] whitespace-pre-wrap opacity-70")}>
        {(value as string | null) ?? ""}
      </div>
    );
  }
  return (
    <RichTextEditorLazy
      value={(value as string | null) ?? ""}
      placeholder={spec.placeholder}
      onChange={(plainText) => onCommit(plainText)}
      minHeight={92}
      maxHeight={260}
    />
  );
};

const JsonRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <textarea
    id={fieldId}
    rows={5}
    spellCheck={false}
    className={cn(
      inputCls,
      "resize-y font-mono text-xs leading-relaxed",
      invalid && "border-destructive focus:ring-destructive",
    )}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder ?? "{ }"}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  />
);

const NumberRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <input
    id={fieldId}
    type="number"
    className={cn(inputCls, "tabular-nums", invalid && "border-destructive focus:ring-destructive")}
    value={value === null || value === undefined ? "" : String(value)}
    placeholder={spec.placeholder}
    min={spec.min}
    max={spec.max}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value === "" ? null : Number(e.target.value))}
    aria-invalid={invalid || undefined}
  />
);

const CurrencyRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <div className="relative">
    <span
      className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 text-ink-3 text-sm"
      aria-hidden
    >
      €
    </span>
    <input
      id={fieldId}
      type="number"
      step="0.01"
      className={cn(
        inputCls,
        "pl-7 tabular-nums",
        invalid && "border-destructive focus:ring-destructive",
      )}
      value={value === null || value === undefined ? "" : String(value)}
      placeholder={spec.placeholder ?? "0,00"}
      min={spec.min}
      max={spec.max}
      readOnly={spec.readonly}
      disabled={disabled}
      onChange={(e) => onCommit(e.target.value === "" ? null : Number(e.target.value))}
      aria-invalid={invalid || undefined}
    />
  </div>
);

const BooleanRenderer: React.FC<RendererProps> = ({ spec, value, disabled, onCommit, fieldId }) => {
  const { i18n } = useLingui();
  return (
    // Native checkbox styled as a switch — keeps the bundle lean (no Radix
    // Switch primitive needed for this single use-case).
    <label className="inline-flex cursor-pointer select-none items-center gap-2">
      <Checkbox
        id={fieldId}
        checked={Boolean(value)}
        disabled={disabled || spec.readonly}
        onChange={(e) => onCommit(e.target.checked)}
      />
      <span className="text-ink-2 text-sm">{spec.placeholder ?? i18n._(msg`Attivo`)}</span>
    </label>
  );
};

// ── Date / Datetime renderer ────────────────────────────────────────────────
//
// SOTA (June 2026): popover-anchored Calendar (shadcn over react-day-picker
// v9) replaces the native `<input type="date">`. The native control was the
// single ugliest control in the HITL action forms (Notion-Calendar /
// Google-Calendar / Cron all moved away from it years ago) — it ignores the
// shell typography, opens an OS chrome that breaks the Italian week
// (Sunday-anchored on macOS), and gives no quick "Oggi" shortcut.
//
// Value shape stays identical for backwards compat with `proposed_fields`
// (parent diff + zod schema): `"YYYY-MM-DD"` for `date`, `"YYYY-MM-DDTHH:mm"`
// for `datetime`. The hidden `<input>` keeps form-submit semantics for the
// rare consumer that wraps `ActionFieldEditor` in a `<form>`.

/** "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm" → Date | null (local-tz aware). */
function parseFieldDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  // Treat as local time, mirroring `<input type="datetime-local">` semantics.
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local-tz "YYYY-MM-DD" (matches the wire format used by the action schema). */
function formatDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local-tz "YYYY-MM-DDTHH:mm" matching the `<input type="datetime-local">` wire format. */
function formatDateTime(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${formatDateOnly(d)}T${hh}:${mm}`;
}

const DateRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => {
  const withTime = spec.type === "datetime";
  const selected = parseFieldDate((value as string | null) ?? "");

  // Shared <DateTimePicker> (primitives) — the single date control. This
  // renderer only bridges the wire format: the picker speaks ``Date | null``,
  // the action schema speaks ``"YYYY-MM-DD"`` / ``"YYYY-MM-DDTHH:mm"``.
  return (
    <DateTimePicker
      id={fieldId}
      value={selected}
      withTime={withTime}
      disabled={disabled}
      readOnly={spec.readonly}
      invalid={invalid}
      placeholder={spec.placeholder}
      triggerClassName={inputCls}
      onChange={(next) =>
        onCommit(next ? (withTime ? formatDateTime(next) : formatDateOnly(next)) : null)
      }
    />
  );
};

const SelectRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <StyledSelect
    id={fieldId}
    className={cn(inputCls, invalid && "border-destructive focus:ring-destructive")}
    value={(value as string | null) ?? ""}
    disabled={disabled || spec.readonly}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  >
    {!spec.required ? <option value="">—</option> : null}
    {(spec.options ?? []).map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </StyledSelect>
);

/**
 * MultiSelect — two modes:
 *   • option toggles when ``spec.options`` is provided (e.g. a Salesforce
 *     multi-picklist), each value clearly checkable.
 *   • free tag input otherwise (e.g. a list of email recipients / @mentions):
 *     type + Enter (or comma) to add, click ✕ to remove.
 * Value is committed as ``string[]`` (crossing the scalar union via the same
 * local cast precedent as ``slide_list``).
 */
const MultiSelectRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => {
  const { i18n } = useLingui();
  const selected = asStringArray(value);
  const [draft, setDraft] = React.useState("");
  const commitArray = (next: string[]) => onCommit(next as unknown as ActionFieldValue);
  const remove = (v: string) => commitArray(selected.filter((s) => s !== v));

  if (spec.options && spec.options.length > 0) {
    const toggle = (v: string) =>
      commitArray(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
    return (
      <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={`${fieldId}-lbl`}>
        {spec.options.map((opt) => {
          const on = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled || spec.readonly}
              onClick={() => toggle(opt.value)}
              aria-pressed={on}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-50",
                on
                  ? "border-accent bg-accent/10 text-accent"
                  : "border-hairline text-ink-2 hover:border-accent/50",
              )}
            >
              {on ? <Check className="mr-1 inline h-3 w-3" aria-hidden /> : null}
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }

  const addDraft = () => {
    const v = draft.trim();
    if (v && !selected.includes(v)) commitArray([...selected, v]);
    setDraft("");
  };
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5 rounded-md border border-hairline bg-card px-2 py-1.5",
        invalid && "border-destructive",
        (disabled || spec.readonly) && "opacity-50",
      )}
    >
      {selected.map((v) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-accent text-xs"
        >
          {v}
          {!disabled && !spec.readonly ? (
            <button type="button" onClick={() => remove(v)} aria-label={i18n._(msg`Rimuovi ${v}`)}>
              <X className="h-3 w-3 hover:text-destructive" />
            </button>
          ) : null}
        </span>
      ))}
      <input
        id={fieldId}
        type="text"
        className="min-w-[120px] flex-1 bg-transparent text-ink-1 text-sm placeholder:text-ink-3 focus:outline-none"
        value={draft}
        placeholder={spec.placeholder ?? i18n._(msg`Aggiungi e premi Invio`)}
        disabled={disabled || spec.readonly}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addDraft();
          } else if (e.key === "Backspace" && draft === "" && selected.length > 0) {
            const last = selected[selected.length - 1];
            if (last !== undefined) remove(last);
          }
        }}
        onBlur={addDraft}
        aria-invalid={invalid || undefined}
      />
    </div>
  );
};

/**
 * Recipients — multi-value chip input with people-search autocomplete.
 *
 * Wired to ``loadPeopleOptions`` (Google People + MS Graph fanout via the BE
 * ``GET /people/search`` endpoint). The autocomplete is scoped to a single
 * connection — the per-capability account picker field whose name lives on
 * ``spec.peopleSearchConnectionFieldName``. When the user has not yet picked
 * the sender (connection id is null) OR the host did not supply a
 * ``loadPeopleOptions`` callback, the renderer degrades to free-text chips
 * (type + Enter / comma to add, click ✕ to remove) — same UX contract as
 * ``MultiSelectRenderer`` tag mode, so the field is always editable.
 *
 * Committed value: ``string[]`` of email addresses. This matches the BE
 * ``payload_schema`` contract for ``to`` / ``cc`` / ``bcc`` / ``attendees``
 * — the same wire format that the email composer and calendar event creator
 * already produce, so no schema migration is needed downstream.
 *
 * Per ``feedback_action_forms_account_picker_first.md``: every action form
 * that touches an external SaaS account MUST autocomplete recipients via
 * the chosen account's people API, not via plain free-text input. This
 * renderer is the single shared primitive (DRY rule) that delivers it.
 */
const RecipientsRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
  loadPeopleOptions,
  peopleSearchConnectionId,
}) => {
  // Delegates to the shared ``RecipientsInput`` primitive so the email
  // envelope (``EmailSendDraftPreview``) and the generic action drawer
  // ship the EXACT same chip + autocomplete UX. DRY anchor — see
  // ``./RecipientsInput.tsx``.
  return (
    <RecipientsInput
      value={asStringArray(value)}
      onChange={(next) => onCommit(next as unknown as ActionFieldValue)}
      id={fieldId}
      ariaLabel={spec.label}
      placeholder={spec.placeholder}
      disabled={disabled}
      readOnly={spec.readonly}
      invalid={invalid}
      loadPeopleOptions={loadPeopleOptions}
      connectionId={peopleSearchConnectionId ?? null}
      field={spec}
    />
  );
};

/**
 * Reference — foreign-key typeahead. When ``loadReferenceOptions`` is wired by
 * the host, debounced queries surface a dropdown of matching records. Without
 * a transport it degrades to a free-text input (with a datalist when static
 * ``options`` exist) so the field is always editable. The committed value is
 * the option ``value`` (the record id); the visible text is its ``label``.
 */
const ReferenceRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
  loadReferenceOptions,
}) => {
  const { i18n } = useLingui();
  const current = (value as string | null) ?? "";
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [results, setResults] = React.useState<readonly ActionFieldOption[]>([]);
  const [label, setLabel] = React.useState<string>(current);

  // Debounced async lookup.
  React.useEffect(() => {
    if (!loadReferenceOptions || !open) return;
    const q = query.trim();
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(() => {
      void loadReferenceOptions(spec, q)
        .then((opts) => {
          if (!cancelled) setResults(opts);
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open, loadReferenceOptions, spec]);

  // Fallback: no async transport → text input (+ datalist when options exist).
  if (!loadReferenceOptions) {
    const listId = `${fieldId}-dl`;
    return (
      <>
        <input
          id={fieldId}
          type="text"
          list={spec.options?.length ? listId : undefined}
          className={cn(inputCls, invalid && "border-destructive focus:ring-destructive")}
          value={current}
          placeholder={
            spec.placeholder ??
            (spec.referenceTo ? i18n._(msg`Cerca ${spec.referenceTo}…`) : undefined)
          }
          readOnly={spec.readonly}
          disabled={disabled}
          onChange={(e) => onCommit(e.target.value)}
          aria-invalid={invalid || undefined}
        />
        {spec.options?.length ? (
          <datalist id={listId}>
            {spec.options.map((o) => (
              <option key={o.value} value={o.value} label={o.label} />
            ))}
          </datalist>
        ) : null}
      </>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <input
          id={fieldId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${fieldId}-listbox`}
          autoComplete="off"
          className={cn(inputCls, "pr-8", invalid && "border-destructive focus:ring-destructive")}
          value={open ? query : label || current}
          placeholder={
            spec.placeholder ??
            (spec.referenceTo ? i18n._(msg`Cerca ${spec.referenceTo}…`) : i18n._(msg`Cerca…`))
          }
          readOnly={spec.readonly}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          aria-invalid={invalid || undefined}
        />
        <span className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-2 text-ink-3">
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </span>
      </div>
      {open ? (
        <ul
          id={`${fieldId}-listbox`}
          className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-hairline bg-card py-1 shadow-lg ring-1 ring-black/5"
        >
          {results.length === 0 ? (
            <li className="px-3 py-2 text-ink-3 text-xs">
              {busy ? i18n._(msg`Ricerca…`) : i18n._(msg`Nessun risultato`)}
            </li>
          ) : (
            results.map((opt) => (
              <li key={opt.value} aria-selected={opt.value === current}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-ink-1 text-sm hover:bg-accent/5"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onCommit(opt.value);
                    setLabel(opt.label);
                    setOpen(false);
                  }}
                >
                  <span>{opt.label}</span>
                  {opt.value === current ? <Check className="h-3.5 w-3.5 text-accent" /> : null}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

/**
 * Runtime narrowing for a ``slide_list`` value. The generic editor types
 * every field value as ``ActionFieldValue`` so slide arrays arrive
 * type-erased; this guard re-narrows them without widening the shared union
 * across the whole action pipeline (audit diff, modifications diff, …).
 */
function isSlideArray(value: unknown): value is SlideObj[] {
  return (
    Array.isArray(value) &&
    value.every(
      (s) => typeof s === "object" && s !== null && "title" in s && "body" in s && "layout" in s,
    )
  );
}

const SlideListRenderer: React.FC<RendererProps> = ({ spec, value, disabled, onCommit }) => {
  const slides = isSlideArray(value) ? value : [];
  const options = (spec.options ?? []).map((o) => o.value);
  return (
    <SlideOutlineEditor
      slides={slides}
      options={options}
      disabled={disabled || spec.readonly}
      onChange={(next) => onCommit(next as unknown as ActionFieldValue)}
    />
  );
};

// ============================================================================
// File list renderer — staged attachments for ``send_email`` etc.
// ============================================================================
//
// Thin bridge: narrows the type-erased ``ActionFieldValue`` to an
// ``AttachmentChipValue[]`` and delegates the whole presentation + staging
// logic to the shared ``<EmailAttachmentField>`` (DRY — the SAME component the
// inbox reply composer renders, so both surfaces share one style + behaviour).

const FileListRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  uploadAttachment,
  searchAttachableDocuments,
}) => {
  const ready: AttachmentChipValue[] = isAttachmentArray(value) ? value : [];
  return (
    <EmailAttachmentField
      value={ready}
      onChange={(next) => onCommit(next as unknown as ActionFieldValue)}
      uploadAttachment={uploadAttachment}
      searchAttachableDocuments={searchAttachableDocuments}
      disabled={disabled || spec.readonly}
    />
  );
};

function renderForType(props: RendererProps): React.ReactNode {
  switch (props.spec.type) {
    case "slide_list":
      return <SlideListRenderer {...props} />;
    case "file":
      return <FileListRenderer {...props} />;
    case "text":
      return <TextRenderer {...props} />;
    case "richtext":
      return <RichTextRenderer {...props} />;
    case "json":
      return <JsonRenderer {...props} />;
    case "number":
      return <NumberRenderer {...props} />;
    case "currency":
      return <CurrencyRenderer {...props} />;
    case "boolean":
      return <BooleanRenderer {...props} />;
    case "date":
    case "datetime":
      return <DateRenderer {...props} />;
    case "select":
      return <SelectRenderer {...props} />;
    case "multiselect":
      return <MultiSelectRenderer {...props} />;
    case "reference":
      return <ReferenceRenderer {...props} />;
    case "recipients":
      return <RecipientsRenderer {...props} />;
    case "phone":
      return <PhoneRenderer {...props} />;
    case "url":
      return <UrlRenderer {...props} />;
    case "password":
      return <PasswordRenderer {...props} />;
    case "iban":
      return <IbanRenderer {...props} />;
    case "vat_id":
      return <VatIdRenderer {...props} />;
    default:
      return <StringRenderer {...props} />;
  }
}

const IbanRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <input
    id={fieldId}
    type="text"
    inputMode="text"
    autoComplete="off"
    className={cn(
      inputCls,
      "font-mono uppercase tracking-wider",
      invalid && "border-destructive focus:ring-destructive",
    )}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder ?? "IT60 X054 2811 1010 0000 0123 456"}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value.toUpperCase())}
    aria-invalid={invalid || undefined}
  />
);

const VatIdRenderer: React.FC<RendererProps> = ({
  spec,
  value,
  disabled,
  onCommit,
  invalid,
  fieldId,
}) => (
  <input
    id={fieldId}
    type="text"
    inputMode="numeric"
    className={cn(inputCls, "tabular-nums", invalid && "border-destructive focus:ring-destructive")}
    value={(value as string | null) ?? ""}
    placeholder={spec.placeholder ?? "12345678901"}
    maxLength={13}
    readOnly={spec.readonly}
    disabled={disabled}
    onChange={(e) => onCommit(e.target.value)}
    aria-invalid={invalid || undefined}
  />
);

// ============================================================================
// Before → after diff (UPDATE_RECORD)
// ============================================================================
//
// When a field carries a ``currentValue`` (the record's live "before", set by
// the BE only for UPDATE_RECORD proposals) AND the operator's edited value
// differs, we render a compact ``current → proposed`` line so they see exactly
// what will change on the CRM / accounting record before confirming. Only
// CHANGED fields render it; unchanged fields look exactly as before. Long text
// fields reuse the shared ``InlineDiffPreview`` (word-level redline) instead of
// the one-line arrow. The field stays fully editable — the diff recomputes live
// as the operator types.

/** Long-form fields get the ``InlineDiffPreview`` redline; scalars the arrow. */
function isLongTextType(type: ActionFieldType): boolean {
  return type === "text" || type === "richtext";
}

/** Numeric-ish fields get ``tabular-nums`` so digits align in the diff. */
function isNumericType(type: ActionFieldType): boolean {
  return type === "number" || type === "currency";
}

/** Render one value for display: map ``select`` options to their label, join
 * arrays, humanise booleans. ``null`` / empty → an em-dash placeholder. */
function useDisplayValue(spec: ActionFieldSpec, value: ActionFieldValue): string {
  const { i18n } = useLingui();
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "—";
  }
  if (typeof value === "boolean") {
    return value ? i18n._(msg`Sì`) : i18n._(msg`No`);
  }
  if ((spec.type === "select" || spec.type === "multiselect") && spec.options) {
    const opt = spec.options.find((o) => o.value === String(value));
    if (opt) return opt.label;
  }
  return String(value);
}

/**
 * A ``currentValue`` is worth diffing only when it's a KNOWN, non-empty
 * "before". This is also the graceful-degradation guard: when the BE couldn't
 * read the live record it sends ``current_value: null`` for the proposed
 * fields — treating null/empty as "no known before" means a failed read (or a
 * field that was previously blank) degrades to the plain proposed value with
 * no misleading ``— → value`` diff.
 */
function hasKnownBefore(v: ActionFieldValue | undefined): v is ActionFieldValue {
  if (v === undefined || v === null || v === "") return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** Structural equality for the before/after comparison (arrays compared by
 * element; ``null``/``""`` normalised so a cleared field reads as changed). */
function fieldValuesEqual(a: ActionFieldValue, b: ActionFieldValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = asStringArray(a);
    const bb = asStringArray(b);
    return aa.length === bb.length && aa.every((x, i) => x === bb[i]);
  }
  const na = a === null || a === undefined ? "" : String(a);
  const nb = b === null || b === undefined ? "" : String(b);
  return na === nb;
}

interface FieldDiffLineProps {
  spec: ActionFieldSpec;
  /** The live edited value (the diff "after"). */
  value: ActionFieldValue;
  /** Restore the field to its current record value (long-text reject control). */
  onRestore: () => void;
}

const FieldDiffLine: React.FC<FieldDiffLineProps> = ({ spec, value, onRestore }) => {
  const { i18n } = useLingui();
  const current = spec.currentValue;
  const beforeText = useDisplayValue(spec, current ?? null);
  const afterText = useDisplayValue(spec, value);

  // Long text → reuse the shared inline redline. A single ordered change
  // covers the whole field: rejecting it restores the current record value
  // (keeps the field editable — the input below stays the source of truth).
  if (isLongTextType(spec.type)) {
    const before = current === null || current === undefined ? "" : String(current);
    const after = value === null || value === undefined ? "" : String(value);
    const changeType: DiffChange["type"] =
      before === "" ? "insert" : after === "" ? "delete" : "replace";
    const changes: readonly DiffChange[] = [
      { id: `${spec.name}-diff`, type: changeType, before, after },
    ];
    return (
      <InlineDiffPreview
        original={before}
        changes={changes}
        decisions={{}}
        onDecision={(_id, decision) => {
          // Reject = discard the edit, keep the record's current value.
          if (decision === "rejected") onRestore();
        }}
        className="rounded-md border border-hairline bg-card px-2 py-1.5"
      />
    );
  }

  // Scalar → compact ``before → after`` line.
  return (
    <p
      className={cn(
        "flex flex-wrap items-center gap-1.5 text-[12px]",
        isNumericType(spec.type) && "tabular-nums",
      )}
      aria-label={i18n._(msg`Modifica: da ${beforeText} a ${afterText}`)}
    >
      <span className="text-ink-3 line-through decoration-rose-500">{beforeText}</span>
      <ArrowRight className="h-3 w-3 shrink-0 text-ink-4" aria-hidden />
      <span className="font-medium text-emerald-700">{afterText}</span>
    </p>
  );
};

// ============================================================================
// Field row
// ============================================================================

interface FieldRowProps {
  spec: ActionFieldSpec;
  value: ActionFieldValue;
  error: ActionFieldError | undefined;
  disabled?: boolean;
  onCommit: (next: ActionFieldValue) => void;
  loadReferenceOptions?: LoadReferenceOptions;
  loadPeopleOptions?: LoadPeopleOptions;
  uploadAttachment?: UploadAttachment;
  searchAttachableDocuments?: SearchAttachableDocuments;
  peopleSearchConnectionId?: string | null;
  lock?: FieldLockInfo;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: (name: string) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({
  spec,
  value,
  error,
  disabled,
  onCommit,
  loadReferenceOptions,
  loadPeopleOptions,
  uploadAttachment,
  searchAttachableDocuments,
  peopleSearchConnectionId,
  lock,
  onFieldFocus,
  onFieldBlur,
}) => {
  const { i18n } = useLingui();
  const invalid = Boolean(error);
  const labelId = `act-fld-${spec.name}-lbl`;
  const fieldId = `act-fld-${spec.name}`;
  // ``boolean`` uses its own wrapping <label>; other types associate the
  // visible label to the control via htmlFor → the rendered input's id.
  const isStandaloneControl = spec.type === "boolean";
  // Confidence dot label (only one branch evaluated → one macro extraction).
  const confidenceLabel =
    spec.confidence === "high"
      ? i18n._(msg`Affidabilità alta`)
      : spec.confidence === "low"
        ? i18n._(msg`Affidabilità da verificare`)
        : null;
  return (
    // onFocus/onBlur bubble (focusin/focusout) → the host acquires the
    // field-lock while this field's subtree holds focus, releases on leave.
    // The ``relatedTarget`` guard fires ONLY when focus actually crosses the
    // field boundary, so hopping between a compound field's sub-controls
    // (select input↔dropdown, chip inputs) never churns the lock.
    <div
      className="flex flex-col gap-1"
      onFocus={
        onFieldFocus
          ? (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                onFieldFocus(spec.name);
            }
          : undefined
      }
      onBlur={
        onFieldBlur
          ? (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFieldBlur(spec.name);
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between gap-2">
        <label
          id={labelId}
          className="flex items-center gap-1.5 font-medium text-ink-2 text-xs"
          htmlFor={isStandaloneControl ? undefined : fieldId}
        >
          <span>{spec.label}</span>
          {spec.required ? (
            <span className="text-accent" aria-label={i18n._(msg`obbligatorio`)}>
              *
            </span>
          ) : null}
          {spec.citation ? (
            <span
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-sm bg-ando-orange-soft px-1 font-semibold text-[10px] text-accent"
              title={i18n._(msg`Estratto da fonte — clicca per aprire`)}
            >
              {spec.citation}
            </span>
          ) : null}
          {spec.confidence && confidenceLabel ? (
            <span
              role="img"
              aria-label={confidenceLabel}
              title={confidenceLabel}
              className={cn(
                "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                spec.confidence === "high" ? "bg-success" : "bg-warning",
              )}
            />
          ) : null}
        </label>
        {lock ? (
          <span
            title={i18n._(msg`In modifica da ${lock.name}`)}
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] text-white"
            style={{ backgroundColor: lock.color }}
          >
            <span aria-hidden>{lock.initials ?? "•"}</span>
            <span className="font-medium normal-case">{lock.name}</span>
          </span>
        ) : spec.readonly ? (
          <span className="text-[10px] text-ink-3 uppercase tracking-wide">
            {i18n._(msg`read-only`)}
          </span>
        ) : null}
      </div>
      {hasKnownBefore(spec.currentValue) && !fieldValuesEqual(spec.currentValue, value) ? (
        <FieldDiffLine
          spec={spec}
          value={value}
          onRestore={() => onCommit(spec.currentValue ?? null)}
        />
      ) : null}
      {renderForType({
        spec,
        value,
        disabled,
        onCommit,
        invalid,
        fieldId,
        loadReferenceOptions,
        loadPeopleOptions,
        uploadAttachment,
        searchAttachableDocuments,
        peopleSearchConnectionId,
      })}
      {error ? (
        <p className="flex items-center gap-1 text-destructive text-xs" role="alert">
          <AlertCircle className="h-3 w-3" />
          {error.message}
        </p>
      ) : spec.helperText ? (
        <p className="flex items-center gap-1 text-ink-3 text-xs">
          <Check className="h-3 w-3 opacity-0" aria-hidden />
          {spec.helperText}
        </p>
      ) : null}
    </div>
  );
};

// ============================================================================
// Grouping
// ============================================================================

interface SectionGroup {
  /** Section heading (``null`` for the ungrouped default group). */
  title: string | null;
  fields: ActionFieldSpec[];
}

/**
 * Partition the schema into the ungrouped default group + named sections,
 * preserving first-appearance order. Action-time fields are extracted
 * separately by the caller and never appear here.
 */
function groupBySection(fields: readonly ActionFieldSpec[]): SectionGroup[] {
  const order: (string | null)[] = [];
  const byKey = new Map<string | null, ActionFieldSpec[]>();
  for (const f of fields) {
    const key = f.section ?? null;
    if (!byKey.has(key)) {
      byKey.set(key, []);
      order.push(key);
    }
    byKey.get(key)?.push(f);
  }
  return order.map((title) => ({ title, fields: byKey.get(title) ?? [] }));
}

// ============================================================================
// Main editor
// ============================================================================

export const ActionFieldEditor: React.FC<ActionFieldEditorProps> = ({
  schema,
  values,
  errors,
  onChange,
  disabled,
  loadReferenceOptions,
  loadPeopleOptions,
  uploadAttachment,
  searchAttachableDocuments,
  className,
  lockedFields,
  onFieldFocus,
  onFieldBlur,
}) => {
  const { i18n } = useLingui();
  const handleCommit = React.useCallback(
    (name: string, next: ActionFieldValue) => {
      onChange({ ...values, [name]: next });
    },
    [values, onChange],
  );

  const renderRow = (spec: ActionFieldSpec) => {
    // For ``recipients`` fields, resolve the connection id of the linked
    // per-capability account picker so the people-search query is scoped
    // to the chosen sender mailbox / calendar / workspace. The renderer
    // falls back to free-text chips when no link is declared OR the user
    // hasn't picked the sender yet.
    const connRaw = spec.peopleSearchConnectionFieldName
      ? values[spec.peopleSearchConnectionFieldName]
      : null;
    const peopleSearchConnectionId =
      typeof connRaw === "string" && connRaw.trim() !== "" ? connRaw : null;
    const lock = lockedFields?.[spec.name];
    return (
      <FieldRow
        key={spec.name}
        spec={spec}
        value={values[spec.name] ?? null}
        error={errors?.[spec.name]}
        disabled={disabled || Boolean(lock)}
        onCommit={(next) => handleCommit(spec.name, next)}
        loadReferenceOptions={loadReferenceOptions}
        loadPeopleOptions={loadPeopleOptions}
        uploadAttachment={uploadAttachment}
        searchAttachableDocuments={searchAttachableDocuments}
        peopleSearchConnectionId={peopleSearchConnectionId}
        lock={lock}
        onFieldFocus={onFieldFocus}
        onFieldBlur={onFieldBlur}
      />
    );
  };

  const actionTime = schema.filter((s) => s.isActionTime);
  const rest = schema.filter((s) => !s.isActionTime);
  const hasSections = rest.some((s) => Boolean(s.section));

  // Backwards-compatible fast path: no action-time fields and no sections →
  // render the legacy flat list (keeps existing previews + tests identical).
  if (actionTime.length === 0 && !hasSections) {
    return <div className={cn("flex flex-col gap-3", className)}>{schema.map(renderRow)}</div>;
  }

  const groups = groupBySection(rest);

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {actionTime.length > 0 ? (
        <section
          aria-labelledby="act-section-actiontime"
          className="rounded-lg border border-accent/40 bg-accent/5 p-3"
        >
          <h3
            id="act-section-actiontime"
            className="mb-0.5 flex items-center gap-1.5 font-semibold text-accent text-xs uppercase tracking-wide"
          >
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
            {i18n._(msg`Verifica questi campi`)}
          </h3>
          <p className="mb-2.5 text-[11px] text-ink-3">
            {i18n._(
              msg`Parametri richiesti al momento dell'esecuzione: controllali con attenzione prima di confermare.`,
            )}
          </p>
          <div className="flex flex-col gap-3">{actionTime.map(renderRow)}</div>
        </section>
      ) : null}

      {groups.map((group, idx) =>
        group.title === null ? (
          <div key="__ungrouped" className="flex flex-col gap-3">
            {group.fields.map(renderRow)}
          </div>
        ) : (
          <section
            key={group.title}
            aria-labelledby={`act-section-${idx}`}
            className="flex flex-col gap-3"
          >
            <h3
              id={`act-section-${idx}`}
              className="border-hairline border-b pb-1 font-semibold text-ink-2 text-xs uppercase tracking-wide"
            >
              {group.title}
            </h3>
            {group.fields.map(renderRow)}
          </section>
        ),
      )}
    </div>
  );
};
ActionFieldEditor.displayName = "ActionFieldEditor";
